import { DateUtil } from '@core/services/date/date.util';
import { parse } from 'date-fns';
import { groupBy } from 'lodash-es';
import {
  AcsConsumption,
  ClimateConsumption,
  ConsumptionInvoicesFilter,
  HousingModel,
  ReportModel,
  RoomModel,
} from '@core/domain/model/consumption-invoices/consumption-invoices.model';
import {
  AcsConsumptionApi,
  ClimateConsumptionApi,
  ConsumptionInvoicesFilterApi,
  DailyAcsConsumptionApi,
  DailyClimateConsumptionApi,
  HousingModelApi,
  MonthlyAcsConsumptionApi,
  MonthlyClimateConsumptionApi,
  ReportModelApi,
  RoomModelApi,
} from '@core/api/model/consumption-invoices/consumption-invoices-api.model';

const DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";
export class ConsumptionInvoicesMapper {
  public static filterToApi(domain: ConsumptionInvoicesFilter): ConsumptionInvoicesFilterApi {
    const apiFilter: ConsumptionInvoicesFilterApi = {
      // ensure PuntoMedida maps from the selected room code
      PuntoMedida: domain.roomCode ?? '0', // use room code if available, fallback to 0
      FechaDesde: DateUtil.toBackString(domain.startDate)!,
      FechaHasta: DateUtil.toBackString(domain.endDate)!,
    };
    console.debug('ConsumptionInvoicesMapper.filterToApi', { roomCode: domain.roomCode, apiFilter });
    // backend only expects PuntoMedida and date range; ignore housing/room ids
    return apiFilter;
  }
  public static climateDailyToDomain(api: ClimateConsumptionApi<DailyClimateConsumptionApi>): Array<ClimateConsumption> {
    return ConsumptionInvoicesMapper.dailyToDomain(api);
  }

  public static climateMonthlyToDomain(api: ClimateConsumptionApi<MonthlyClimateConsumptionApi>, year: number): Array<ClimateConsumption> {
    const consumptionList = api
      .flatMap((value) => value.consumos)
      .flatMap((monthConsumption) =>
        monthConsumption.datosConsumos.map((daysConsumptions) => {
          const month = monthConsumption.mes.padStart(2, '0');
          const day = daysConsumptions.dia.padStart(2, '0');

          const dateString = `${year}-${month}-${day}T00:00:00`;

          return {
            dateString,
            value: daysConsumptions.consumo,
          };
        })
      );

    return ConsumptionInvoicesMapper.mapToDomain<ClimateConsumption>(consumptionList);
  }

  private static dailyToDomain<T extends { dia: string; datosConsumos: { hora: string; consumo: number }[] }>(
    api: Array<{ cups: string; consumos: T }>
  ): Array<{ date: Date; hour: string; value: number }> {
    const consumptionList = api
      .flatMap((value) => value.consumos)
      .flatMap((dayConsumption) =>
        dayConsumption.datosConsumos.map((hoursConsumptions) => ({
          dateString: `${dayConsumption.dia}___${hoursConsumptions.hora}`,
          value: hoursConsumptions.consumo,
        }))
      );

    const grouped = groupBy(consumptionList, (value) => value.dateString);

    return Object.entries(grouped).map(([dateString, value]) => {
      const dateStringSplit = dateString.split('___');
      const date = `${dateStringSplit[0]}T00:00:00`;
      const hour = dateStringSplit[1];

      return {
        date: parse(date, DATE_FORMAT, new Date()),
        hour,
        value: value.reduce((acc, currentValue) => acc + currentValue.value, 0),
      };
    });
  }

  public static acsDailyToDomain(api: AcsConsumptionApi<DailyAcsConsumptionApi>): Array<AcsConsumption> {
    return ConsumptionInvoicesMapper.dailyToDomain(api);
  }

  private static mapToDomain<T extends { date: Date; value: number }>(consumptionList: Array<{ dateString: string; value: number }>): T[] {
    const consumptionMap = consumptionList.reduce(
      (acc, currentValue) => {
        const key = currentValue.dateString;
        let value = currentValue.value;

        if (acc[key] != null) {
          value += acc[key];
        }

        return {
          ...acc,
          [key]: value,
        };
      },
      {} as Record<string, number>
    );

    return Object.entries(consumptionMap).map(([dateString, value]) => ({
      date: parse(dateString, DATE_FORMAT, new Date()),
      value,
    })) as T[];
  }

  public static acsMonthlyToDomain(api: AcsConsumptionApi<MonthlyAcsConsumptionApi>, year: number): Array<AcsConsumption> {
    const consumptionList = api
      .flatMap((value) => value.consumos)
      .flatMap((monthConsumption) =>
        monthConsumption.datosConsumos.map((daysConsumptions) => {
          const month = monthConsumption.mes.padStart(2, '0');
          const day = daysConsumptions.dia.padStart(2, '0');

          const dateString = `${year}-${month}-${day}T00:00:00`;

          return {
            dateString,
            value: daysConsumptions.consumo,
          };
        })
      );

    return ConsumptionInvoicesMapper.mapToDomain<AcsConsumption>(consumptionList);
  }

  public static housingFromApi(input: HousingModelApi | HousingModelApi[]): HousingModel[] {
    if (Array.isArray(input)) {
      return input.map((item) => ({
        codeHousing: item.codigo,
        nameHousing: item.nombre,
        idHousing: item.idVivienda,
      }));
    }
    return [
      {
        codeHousing: input.codigo,
        nameHousing: input.nombre,
        idHousing: input.idVivienda,
      },
    ];
  }

  public static roomFromApi(input: RoomModelApi | RoomModelApi[]): RoomModel[] {
    if (Array.isArray(input)) {
      return input.map((item) => ({
        idRoom: item.idHabitacion,
        codeRoom: item.codigo,
        nameRoom: item.nombre,
        idHousing: item.idVivienda,
      }));
    }
    return [
      {
        idRoom: input.idHabitacion,
        codeRoom: input.codigo,
        nameRoom: input.nombre,
        idHousing: input.idVivienda,
      },
    ];
  }

  public static reportsFromApi(api: ReportModelApi[]): ReportModel[] {
    return api.map((item) => ({
      reportCode: item.codigo,
      fileName: item.nombre,
      startDate: item.fechaInicio,
      endDate: item.fechaFin,
      idHousing: item.idVivienda,
      idReport: item.idInforme,
    }));
  }
}
